import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IGetBookingsCommonResponse } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { faCogs, faPlane, faUser, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Yii } from "libraries/interfaces/Yii";
import { EnumsResourceService } from "libraries/service/booking/enums/enums-resource.service";
import {
    IQualityCheckDataDictionary,
    IQualityCheckSectionDictionaryItem
} from "libraries/service/booking/IQualityCheck";
import { ESashaPermissions } from "libraries/enums/e-sasha-permissions";
import { ILoader } from "../loader/ILoader";
import * as moment from "moment";


interface ITraveller {
    firstName?: string;
    lastName?: string;
    xTitle?: string;
}

export interface ISector {
    fromAirportIata?: string;
    toAirportIata?: string;
    flightNumber?: string;
    beginValue?: string;
    beginTime?: string;
    carrierIata?: string;
    seatCode?: string;
}

interface IAdditional {
    key?: string;
    value?: string;
}

interface IBooking {
    source?: string;
    code?: string;
    owner?: string;
}

export interface IOperationArrayData {
    action?: IAction;
    actionTitle?: string;
    status?: string;
    code?: string;
    control?: string;
    reasonArray?: string[];
    travellerArray?: ITraveller[];
    sectorArray?: ISector[];
    additionalDataArray?: IAdditional[];
    bookingArray?: IBooking[];
    severity?: string;
    nbOccurence?: number;
}

interface IAction {
    xPurpose: string;
}

class Labels {
    public readonly undefinedControl: string = Yii.t('wui-shared-js', 'Contrôle non défini');
    public readonly ref: string = Yii.t('wui-shared-js', 'Ref MO:');
}

@Component({
    selector: 'booking-quality-control-data',
    template: `
        <loader *ngIf="loader.active" [message]="loader.message"></loader>
        <ng-container *ngIf="arrayData?.length">
            <table *permissionIf="sashaPermissions.SASHA_QUALITY_CHECK_SHOW">
                <tbody>
                <ng-container *ngFor="let data of arrayData">
                    <tr *ngIf=" data?.code | isInArrayPipe: displayCode">
                        <th [attr.colspan]="isSummary ? 1 : 6" class="table-header code">{{ data?.code }}</th>
                    </tr>
                    <tr *ngIf="!isSummary ">
                        <td-text [isTop]="true">
                            <booking-operation-result-icon
                                    [result]="data?.severity | severityStatusToIcon"></booking-operation-result-icon>
                            <ng-container>
                                {{ data?.control || labels.undefinedControl }}
                            </ng-container>
                        </td-text>
                        <td-text [isTop]="true">
                            <ul>
                                <li *ngFor="let traveller of data.travellerArray">
                                    <fa-icon *ngIf="traveller.xTitle"
                                             [icon]="traveller?.xTitle | travellerGenderIcon"></fa-icon>
                                    <fa-icon *ngIf="!traveller.xTitle && traveller" [icon]="iconBooking"></fa-icon>
                                    {{ traveller?.firstName }} {{ traveller?.lastName }}
                                </li>
                                <li *ngFor="let sector of data?.sectorArray">
                                    <div>
                                        <fa-icon [icon]="iconSectorAir"></fa-icon>
                                        {{ sector?.fromAirportIata }}
                                        -{{ sector?.toAirportIata }} {{ sector?.carrierIata }}{{ sector?.flightNumber }}
                                        ({{ sector?.seatCode }})
                                    </div>
                                    <div>
                                        <div class="no-icon"></div>
                                        {{ sector?.beginValue }}, {{ sector?.beginTime }}
                                    </div>
                                </li>
                                <li *ngFor="let booking of data?.bookingArray">
                                    <div *ngIf="booking && booking?.source === midOffice">
                                        <div class="no-icon"></div>
                                        {{ labels.ref }} <strong>{{ booking?.code }}</strong>
                                    </div>
                                </li>
                            </ul>
                        </td-text>
                        <td-text [isTop]="true">
                            <ul>
                                <li class="style" *ngFor="let reason of data?.reasonArray">{{ reason }}</li>
                            </ul>
                        </td-text>
                        <td-text [isTop]="true">
                            <ul>
                                <li>
                                    <div>
                                        <fa-icon *ngIf="data.actionTitle" [icon]="iconAction"></fa-icon>
                                        <strong class="icon"> {{ data.actionTitle}}</strong>
                                    </div>
                                </li>
                                <li *ngFor="let additional of data?.additionalDataArray">{{ additional?.key }} :
                                    <strong>{{additional?.value}}</strong></li>
                            </ul>
                        </td-text>
                    </tr>
                    <tr *ngIf="isSummary">
                        <td-text [isTop]="true">
                            <booking-operation-result-icon
                                    [result]="data?.severity | severityStatusToIcon"></booking-operation-result-icon>
                            <ng-container>
                                {{ data?.control || labels.undefinedControl }}
                                <ng-container *ngIf="data?.nbOccurence > 1">(x{{data.nbOccurence}})</ng-container>
                            </ng-container>
                        </td-text>
                    </tr>
                </ng-container>
                </tbody>
            </table>
        </ng-container>

    `,
    styles: [`

        td {
            vertical-align: top;
        }

        .table-header {
            background: white;
            padding: .75rem;
            margin: 0;
        }

        .code {
            font-size: 18px;
        }

        fa-icon, .no-icon {
            width: 16px;
            display: inline-block;
        }

        .icon {
            padding-left: 5px;
        }

        .style {
            list-style: inside;
        }
    `]
})
export class BookingQualityControlDataComponent implements OnChanges {
    @Input()
    public booking: IGetBookingsCommonResponse;
    @Input()
    public isSummary?: boolean = false;
    @Input()
    public isQualityCheckResponse?: boolean = false;

    public labels: Labels = new Labels();

    public readonly iconSectorAir: IconDefinition = faPlane;

    public readonly iconBooking: IconDefinition = faUser;

    public readonly iconAction: IconDefinition = faCogs;

    public arrayData: IOperationArrayData[];

    public displayCode: string[] = [];

    public midOffice: string = "MidOffice";

    public section: string[] = [];

    public sectionDictionnary: { [key: string]: string } = {};

    public sashaPermissions: typeof ESashaPermissions = ESashaPermissions;

    public loader: ILoader = {
        message: Yii.t("wui-sasha-js", "Chargement des messages"),
        active: true
    };

    constructor(
        private readonly enumsResourceService: EnumsResourceService,
        private cdr: ChangeDetectorRef,
    ) {
    }


    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.booking.currentValue) {
            this.displayCode = [];
            this.section = [];
            this.sectionDictionnary = {};
            const operationArrayFromSearch: IOperationArrayData[] = this.booking?.booking?.qualityCheck?.operationsLastPerformed?.operationArray;
            const operationArrayFromQualityCheckResponse: IOperationArrayData[] = this.booking?.operationArray;
            const operationArray: IOperationArrayData[] = this.isQualityCheckResponse ? operationArrayFromQualityCheckResponse : operationArrayFromSearch;
            if (operationArray?.length) {
                this.initSectionDictionary();
            }else{
                this.loader.active = false;
            }

        }
    }

    public isOperationArrayDataValid(operationArrayData: IOperationArrayData): boolean {
        const invalidStatus: string[] = ['SUCCESS', 'ACTION_PERFORMED', 'NO_ACTION_REQUIRED', 'NO_MORE_ACTION_REQUIRED'];
        const invalidSeverity: string[] = ['SUCCESS', 'ACTION_PERFORMED', 'NO_ACTION_REQUIRED'];
        return !invalidStatus.includes(operationArrayData.status.toUpperCase()) && !invalidSeverity.includes(operationArrayData.severity.toUpperCase());
    }

    public initOperationArrayData(): void {
        const arrayDataBis: IOperationArrayData[] = [];
        const operationArrayFromSearch: IOperationArrayData[] = this.booking?.booking?.qualityCheck?.operationsLastPerformed?.operationArray;
        const operationArrayFromQualityCheckResponse: IOperationArrayData[] = this.booking?.operationArray;

        const operationArray: IOperationArrayData[] = this.isQualityCheckResponse ? operationArrayFromQualityCheckResponse : operationArrayFromSearch;

        if (operationArray?.length) {
            operationArray.forEach((operationArrayData: IOperationArrayData): void => {
                const travellerArrayData: ITraveller[] = [];
                const sectorArrayData: ISector[] = [];
                const additionalArrayData: IAdditional[] = [];
                const bookingArrayData: IBooking[] = [];
                if (this.isOperationArrayDataValid(operationArrayData)) {
                    operationArrayData?.travellerArray?.forEach((traveller: ITraveller): void => {
                        travellerArrayData.push({
                            firstName: traveller.firstName,
                            lastName: traveller.lastName,
                            xTitle: traveller.xTitle
                        });
                    });
                    operationArrayData?.sectorArray?.forEach((sector: ISector): void => {
                        sectorArrayData.push({
                            fromAirportIata: sector.fromAirportIata,
                            toAirportIata: sector.toAirportIata,
                            flightNumber: sector.flightNumber,
                            seatCode: sector.seatCode,
                            carrierIata: sector.carrierIata,
                            beginValue: moment(sector.beginValue).format('DD MMMM YYYY'),
                            beginTime: moment(sector.beginValue).format('HH:mm')
                        });
                    });
                    operationArrayData?.additionalDataArray?.forEach((additional: IAdditional): void => {
                        additionalArrayData.push({
                            key: additional.key,
                            value: additional.value
                        });
                    });
                    operationArrayData?.bookingArray?.forEach((booking: IBooking): void => {
                        bookingArrayData.push({
                            source: booking.source,
                            code: booking.code,
                            owner: booking.owner
                        });
                    });
                    arrayDataBis.push({
                        action: operationArrayData.action,
                        actionTitle: this.getActionDictionary(operationArrayData.action),
                        code: this.getOperationDictionary(operationArrayData.code),
                        control: this.getControlDictionary(operationArrayData.control),
                        reasonArray: this.getReasonDictionary(operationArrayData.reasonArray),
                        severity: operationArrayData.severity,
                        travellerArray: travellerArrayData,
                        sectorArray: sectorArrayData,
                        additionalDataArray: this.getAdditionalDataDictionary(additionalArrayData),
                        bookingArray: bookingArrayData
                    });
                }
            });
        }
        if (!this.isSummary) {
            this.arrayData = [...arrayDataBis.sort((a: IOperationArrayData, b: IOperationArrayData): number => a.code.localeCompare(b.code))];
        } else if (this.isSummary) {
            // https://projets2.afidium.com/issues/35475 remove dupes based on controls
            //sort items
            let sorted: IOperationArrayData[] =
                [...arrayDataBis.sort((a: IOperationArrayData, b: IOperationArrayData): number => a.code.localeCompare(b.code))];

            //add occurence qtt with the same control string
            sorted = sorted
                .map(
                    (item: IOperationArrayData): IOperationArrayData => {
                        const nbOccurence: number = sorted.filter((item2: IOperationArrayData): boolean => item2.control === item.control).length;
                        return {
                            ...item,
                            nbOccurence
                        };
                    });

            //remove dupes
            const ids: string[] = sorted.map((item: IOperationArrayData): string => item.control);
            this.arrayData = sorted
                .filter((item: IOperationArrayData, index: number): boolean => {
                    return !ids.includes(item.control, index + 1);
                });
        }
    }

    private initSectionDictionary(): void {
        this.loader.active = true;
        this.enumsResourceService.listQualityCheckSection()
            .subscribe((qualityCheckSectionDictionary: IQualityCheckDataDictionary): void => {
                if (qualityCheckSectionDictionary?.dictionary?.qualityCheckReasonArray?.length) {
                    qualityCheckSectionDictionary?.dictionary?.qualityCheckReasonArray
                        .forEach((reason: IQualityCheckSectionDictionaryItem): void => {
                            this.sectionDictionnary[reason.code] = reason?.nameArray?.length ?
                                reason.nameArray[0].value : reason.code;
                        });
                }
                if (qualityCheckSectionDictionary?.dictionary?.qualityCheckKeyForAdditionalDataArray?.length) {
                    qualityCheckSectionDictionary?.dictionary?.qualityCheckKeyForAdditionalDataArray
                        .forEach((additionalData: IQualityCheckSectionDictionaryItem): void => {
                            this.sectionDictionnary[additionalData.code] = additionalData?.nameArray?.length ?
                                additionalData.nameArray[0].value : additionalData.code;
                        });
                }
                if (qualityCheckSectionDictionary?.dictionary?.qualityCheckOperationArray?.length) {
                    qualityCheckSectionDictionary?.dictionary?.qualityCheckOperationArray
                        .forEach((operation: IQualityCheckSectionDictionaryItem): void => {
                            this.sectionDictionnary[operation.code] = operation?.nameArray?.length ? operation.nameArray[0].value : operation.code;
                        });
                }
                if (qualityCheckSectionDictionary?.dictionary?.qualityCheckControlArray?.length) {
                    qualityCheckSectionDictionary?.dictionary?.qualityCheckControlArray
                        .forEach((control: IQualityCheckSectionDictionaryItem): void => {
                            this.sectionDictionnary[control.code] = control?.nameArray?.length ? control.nameArray[0].value : control.code;
                        });
                }
                if (qualityCheckSectionDictionary?.dictionary?.qualityCheckActionArray?.length) {
                    qualityCheckSectionDictionary?.dictionary?.qualityCheckActionArray
                        .forEach((action: IQualityCheckSectionDictionaryItem): void => {
                            this.sectionDictionnary[action.code] = action?.nameArray?.length ? action.nameArray[0].value : action.code;
                        });
                }
                this.initOperationArrayData();

                this.loader.active = false;
                this.arrayData = [...this.arrayData];
                this.cdr.detectChanges();
            });
    }

    private getActionDictionary(action: IAction): string {
        return this.sectionDictionnary[action.xPurpose.toUpperCase()] ?? action.xPurpose;
    }

    private getOperationDictionary(code: string): string {
        return this.sectionDictionnary[code] ?? code;
    }

    private getControlDictionary(control: string): string {
        return this.sectionDictionnary[control] ?? control;
    }

    private getReasonDictionary(reasonArray: string[]): string[] {
        return reasonArray.map((reason: string): string => {
            return this.sectionDictionnary[reason] ?? reason;
        });
    }

    private getAdditionalDataDictionary(additionalArrayData: IAdditional[]): IAdditional[] {
        return additionalArrayData.map((additionalData: IAdditional): IAdditional => {
            return {
                key: this.sectionDictionnary[additionalData.key] ?? additionalData.key,
                value: additionalData.value
            };
        });
    }
}
